import { useEffect, useRef, useState } from "react";

import { resolveProfilePicUrl } from "../helpers/resolveMediaUrl";
import {
	enqueueProfilePicSync,
	isSocketIoMisroutePayload,
} from "../helpers/profilePicSyncQueue";
import api from "../services/api";

const syncedContactIds = new Set();
const PROFILE_PIC_SYNC_TIMEOUT_MS = 12000;

function resolveDisplayUrl(rawUrl) {
	const trimmed = String(rawUrl || "").trim();
	if (!trimmed || trimmed === "null" || trimmed === "undefined") {
		return "";
	}
	if (/^https?:\/\//i.test(trimmed) || trimmed.startsWith("data:") || trimmed.startsWith("blob:")) {
		return trimmed;
	}
	return resolveProfilePicUrl(trimmed);
}

export default function useContactProfilePic({
	contactId,
	profilePicUrl,
	channel,
	enableSync = true,
}) {
	const [src, setSrc] = useState(() => resolveDisplayUrl(profilePicUrl));
	const [failed, setFailed] = useState(false);
	const syncingRef = useRef(false);

	useEffect(() => {
		setFailed(false);
		setSrc(resolveDisplayUrl(profilePicUrl));
	}, [profilePicUrl, contactId]);

	useEffect(() => {
		if (!enableSync || src || failed || !contactId || syncingRef.current) {
			return undefined;
		}

		const ch = String(channel || "").toLowerCase();
		if (ch && ch !== "whatsapp" && !ch.includes("whatsapp")) {
			return undefined;
		}

		if (syncedContactIds.has(contactId)) return undefined;

		syncingRef.current = true;
		let cancelled = false;

		const sync = async () => {
			const requestPath = `/contacts/${contactId}/profile-picture`;
			try {
				const { data } = await enqueueProfilePicSync(() =>
					api.post(requestPath, {}, { timeout: PROFILE_PIC_SYNC_TIMEOUT_MS })
				);
				if (cancelled) return;
				if (isSocketIoMisroutePayload(data)) {
					syncedContactIds.add(contactId);
					setFailed(true);
					return;
				}
				syncedContactIds.add(contactId);
				const nextUrl = resolveDisplayUrl(
					data?.profilePicUrl || data?.profilePicURL || data?.contact?.profilePicUrl
				);
				if (nextUrl) {
					setSrc(nextUrl);
				}
			} catch {
				if (!cancelled) {
					syncedContactIds.add(contactId);
					setFailed(true);
				}
			} finally {
				syncingRef.current = false;
			}
		};

		sync();

		return () => {
			cancelled = true;
		};
	}, [contactId, channel, src, failed, enableSync]);

	const handleError = () => {
		setFailed(true);
		setSrc("");
	};

	return { src, failed, handleError };
}
